import { Cell } from '@pelckmans/elementis/components/table';
import { TFunction } from 'i18next';
import { CurrentOrPreviousGroup, Member } from '../../../../../../api/groups';
import { LessonResults, Score } from '../../../api/results';
import { ScorableItem } from '../../../api/scorable-items';
import AbsoluteScoreCell from '../../table/cells/AbsoluteScoreCell';
import ActionCell from '../../table/cells/ActionCell';
import DateCell from '../../table/cells/DateCell';
import PercentageCell from '../../table/cells/PercentageCell';
import TimeCell from '../../table/cells/TimeCell';
import { AccumulatedRow, HeaderRow, TableDataGroup, TableDataRow } from '../../types';

type ScorableItemWithAssignment = ScorableItem & { isAssignment: boolean };
type ScoreWithMemberId = Score & { memberId: string };
type AccumulatedScore = Pick<Score, 'score'> & { pending: number; id: string };

const DETAIL_VIEW_COLUMNS = [
  { id: 'head', titleKey: 'PAGES.RESULTS.HEADERS.RESULTS' },
  { id: 'percentage', titleKey: 'PAGES.RESULTS.HEADERS.PERCENTAGE' },
  { id: 'score', titleKey: 'PAGES.RESULTS.HEADERS.SCORE' },
  { id: 'time-spent', titleKey: 'PAGES.RESULTS.HEADERS.TIME_SPENT' },
  { id: 'date-submitted', titleKey: 'PAGES.RESULTS.HEADERS.DATE_SUBMITTED' },
  { id: 'actions', titleKey: '' },
] as const;

export function createTableHeader(t: TFunction, usedScorableItems: ScorableItemWithAssignment[]): HeaderRow {
  return {
    title: t('PAGES.RESULTS.PER_EXERCISE.TABLE_HEADER'),
    columns: usedScorableItems.map(item => ({
      id: `${item.id}${item.isAssignment ? '-assignment' : ''}`,
      title: item.name,
    })),
  };
}

function getMemberScores(lessonData: LessonResults, memberId: string): Score[] {
  return Object.entries(lessonData.scores).flatMap(([nodeId, memberMap]) => memberMap[memberId] || []);
}

function findScoreForItem(memberScores: Score[], scorableItem: ScorableItemWithAssignment): Score | undefined {
  return memberScores.find(x => x.exerciseId === scorableItem.id && x.isAssignment === scorableItem.isAssignment);
}

function getScoresToAccumulate(
  members: Member[],
  lessonData: LessonResults,
  scorableItem: ScorableItemWithAssignment,
): ScoreWithMemberId[] {
  return members.flatMap(member => {
    const memberScores = getMemberScores(lessonData, member.id);
    const score = findScoreForItem(memberScores, scorableItem);
    return score ? { ...score, memberId: member.id } : [];
  });
}

function calculateAccumulatedScore(scoresToAccumulate: ScoreWithMemberId[]): AccumulatedScore | undefined {
  const accumulatedScore = scoresToAccumulate.reduce<AccumulatedScore | null>((acc, curr) => {
    if (!acc && curr) {
      acc = {
        id: `${curr.exerciseId}${curr.memberId}${curr.isAssignment}`,
        pending: 0,
        score: { achievable: 0, achieved: 0, maximum: 0 },
      };
    }

    if (curr) {
      if (curr.needsGrading) {
        return {
          ...acc!,
          pending: acc!.pending + curr.score.maximum,
          score: {
            maximum: acc!.score.maximum + curr.score.maximum,
            achievable: acc!.score.achievable,
            achieved: acc!.score.achieved,
          },
        };
      }

      return {
        ...acc!,
        pending: acc!.pending,
        score: {
          maximum: acc!.score.maximum + curr.score.maximum,
          achievable: acc!.score.achievable + curr.score.achievable,
          achieved: acc!.score.achieved + curr.score.achieved,
        },
      };
    }

    return acc;
  }, null);

  return accumulatedScore || undefined;
}

function createAccumulatedRow(
  group: CurrentOrPreviousGroup,
  members: Member[],
  lessonData: LessonResults,
  usedScorableItems: ScorableItemWithAssignment[],
): AccumulatedRow {
  return {
    id: group.id,
    title: group.name,
    level: 0,
    columns: usedScorableItems.map(item => ({
      title: '',
      ...calculateAccumulatedScore(getScoresToAccumulate(members, lessonData, item))!,
    })),
  };
}

function createMemberRow(
  group: CurrentOrPreviousGroup,
  member: Member,
  lessonData: LessonResults,
  usedScorableItems: ScorableItemWithAssignment[],
): TableDataRow {
  const memberScores = getMemberScores(lessonData, member.id);

  return {
    id: `${group.id}-${member.id}`,
    title: `${member.lastName} ${member.firstName}`,
    level: 1,
    columns: usedScorableItems.map(item => {
      const score = findScoreForItem(memberScores, item);
      return score
        ? { ...score, title: `${item.id}-${item.isAssignment ? '-assignment' : ''}`, pending: 0 }
        : undefined;
    }),
  };
}

function createGroupRow(
  group: CurrentOrPreviousGroup,
  members: Member[],
  lessonData: LessonResults,
  usedScorableItems: ScorableItemWithAssignment[],
): TableDataGroup {
  return {
    id: group.id,
    accumulatedRow: createAccumulatedRow(group, members, lessonData, usedScorableItems),
    rows: members.map(member => createMemberRow(group, member, lessonData, usedScorableItems)),
  };
}

export function buildTableRows(
  lessonDataPerGroup: Record<string, LessonResults>,
  displayedGroups: CurrentOrPreviousGroup[],
  members: Record<string, Member[]>,
  usedScorableItems: ScorableItemWithAssignment[],
): TableDataGroup[] {
  return Object.entries(lessonDataPerGroup)
    .map(([groupId, lessonData]) => {
      const group = displayedGroups.find(g => g.id === groupId);
      const groupMembers = members[groupId];

      if (!group || !groupMembers) {
        return undefined;
      }

      return createGroupRow(group, groupMembers, lessonData, usedScorableItems);
    })
    .filter((row): row is NonNullable<typeof row> => row !== undefined);
}

export function createTableHeaderForDetailView(t: TFunction): HeaderRow {
  return {
    title: t('PAGES.RESULTS.PER_EXERCISE.TABLE_HEADER'),
    columns: DETAIL_VIEW_COLUMNS.map(column => ({
      id: column.id,
      title: column.titleKey ? t(column.titleKey) : '',
    })),
  };
}

function createDetailViewRow(
  member: Member,
  score: Score | null,
  scorableItem: ScorableItemWithAssignment,
  group: CurrentOrPreviousGroup,
): TableDataRow {
  return {
    id: member.id,
    title: `${member.lastName} ${member.firstName}`,
    level: 1,
    columns: [
      score
        ? {
            title: 'results',
            ...score,
            pending: 0,
          }
        : undefined,
      {
        id: 'percentage',
        title: 'percentage',
        Cell: () => <PercentageCell achieved={score?.score.achieved} maximum={score?.score.maximum} />,
      },
      {
        id: 'score',
        title: 'score',
        Cell: () => <AbsoluteScoreCell scorableItem={scorableItem} score={score?.score} />,
      },
      {
        id: 'time-spent',
        title: 'time spent',
        Cell: () => <TimeCell duration={score?.duration || undefined} />,
      },
      {
        id: 'date-submitted',
        title: 'date submitted',
        Cell: () => (
          <DateCell
            completedBySystem={score?.completedBySystem ?? false}
            isZeroScore={score?.isZeroScore ?? false}
            achievedAt={score?.achievedAt}
          />
        ),
      },
      {
        id: 'actions',
        title: 'actions',
        Cell: () => (
          <ActionCell
            scorableItem={scorableItem}
            memberId={member.id}
            groupId={group.id}
            isAssignment={scorableItem.isAssignment}
          />
        ),
      },
    ],
  };
}

/**
 * Returns the data for the group row in the detail view mode.
 * Details can be found here:
 * https://pelckmans.atlassian.net/wiki/spaces/PAK/pages/4307517453/Ianua+Result+Data+and+Visualization
 */
function createDetailViewAccumulatedRow(
  group: CurrentOrPreviousGroup,
  members: Member[],
  lessonScores: LessonResults,
  scorableItem: ScorableItemWithAssignment,
): AccumulatedRow {
  const scoresToAccumulate = getScoresToAccumulate(members, lessonScores, scorableItem);
  const totalScore = calculateAccumulatedScore(scoresToAccumulate)!;

  return {
    id: group.id,
    title: group.name,
    level: 0,
    columns: [
      {
        ...totalScore,
        title: 'results',
        id: 'results-accumulated',
      },
      {
        // https://pelckmans.atlassian.net/wiki/spaces/PAK/pages/4307517453/Ianua+Result+Data+and+Visualization#Aggregate-Score-percentage
        id: 'percentage',
        title: 'percentage',
        Cell: () => {
          return <Cell highlighted />;
        },
      },
      {
        // https://pelckmans.atlassian.net/wiki/spaces/PAK/pages/4307517453/Ianua+Result+Data+and+Visualization#Aggregate-Score-points
        id: 'score',
        title: 'score',
        Cell: () => <Cell highlighted />,
      },
      {
        // https://pelckmans.atlassian.net/wiki/spaces/PAK/pages/4307517453/Ianua+Result+Data+and+Visualization#Time
        id: 'time-spent',
        title: 'time spent',
        Cell: () => {
          const totalDuration = scoresToAccumulate.reduce((acc, curr) => {
            if (curr.duration) {
              acc += curr.duration;
            }
            return acc;
          }, 0);

          return <TimeCell duration={totalDuration} highlighted />;
        },
      },
      {
        id: 'date-submitted',
        title: 'date submitted',
        Cell: () => <Cell highlighted />,
      },
      {
        id: 'actions',
        title: 'actions',
        Cell: () => <Cell highlighted />,
      },
    ],
  };
}

/**
 * Returns the data for the group and its members in the detail view mode.
 */
export function createDetailViewGroupRow(
  group: CurrentOrPreviousGroup,
  members: Member[],
  lessonScores: LessonResults,
  scorableItem: ScorableItemWithAssignment,
): TableDataGroup | undefined {
  const memberRows = members.reduce<TableDataRow[]>((rows, member) => {
    const memberScores = getMemberScores(lessonScores, member.id);
    const score = findScoreForItem(memberScores, scorableItem);

    if (score) rows.push(createDetailViewRow(member, score, scorableItem, group));

    return rows;
  }, []);

  if (memberRows.length === 0) {
    return undefined;
  }

  return {
    id: group.id,
    accumulatedRow: createDetailViewAccumulatedRow(group, members, lessonScores, scorableItem),
    rows: memberRows,
  };
}

export function buildDetailViewGroupedTableRows(
  lessonScoresPerGroup: Record<string, LessonResults>,
  displayedGroups: CurrentOrPreviousGroup[],
  members: Record<string, Member[]>,
  scorableItem: ScorableItemWithAssignment,
): TableDataGroup[] {
  return Object.entries(lessonScoresPerGroup)
    .map(([groupId, lessonScores]) => {
      const group = displayedGroups.find(g => g.id === groupId);
      const groupMembers = members[groupId];

      if (!group || !groupMembers) {
        return undefined;
      }

      return createDetailViewGroupRow(group, groupMembers, lessonScores, scorableItem);
    })
    .filter((row): row is NonNullable<typeof row> => row !== undefined);
}
